import IconCompare from '@/assets/icons/icon-compare.svg';
import IconCompareActive from '@/assets/icons/compare-active.svg';
import {
  AddFavoriteMutation,
  AddVariantToComparisonMutation,
  RemoveFavoriteMutation,
  RemoveVariantFromComparisonMutation,
  useAddFavoriteMutation,
  useAddVariantToComparisonMutation,
  useRemoveFavoriteMutation,
  useRemoveVariantFromComparisonMutation,
  useUserFavoritesQuery,
} from '@/graphql';
import comparisonStore from '@/zustand/comparison';
import { favoritesStore } from '@/zustand/favourite';
import { motion } from 'framer-motion';
import { IoHeartOutline, IoHeartSharp } from 'react-icons/io5';
import { Button } from 'rsuite';
import styles from './product.module.scss';
import useHasMounted from '@/hooks/useHasMounted';
import { useGuestStore } from '@/zustand/guestStore';
import useUserStore from '@/zustand/useUser';

export default function Favorite({ id }: { id: string }) {
  const favorites = favoritesStore(e => e.favorites);
  const setFavorites = favoritesStore(e => e.setFavorites);

  const setComparison = comparisonStore(e => e.setComparison);
  const comparison = comparisonStore(e => e.comparison);

  const userData = useUserStore((user: any) => user?.user);
  const isLoggedIn = Boolean(userData?.id);
  const guestFavoriteListArray = useGuestStore(state => state.favoriteList);

  // const isFavorite = favorites?.some(
  //   (favorite: any) => favorite.node.id === id
  // );
  // const isInComparison = comparison?.some(
  //   (favorite: any) => favorite.id === id
  // );

  const {
    data: favoriteData,
    loading,
    refetch: refetchFavoriteData,
  } = useUserFavoritesQuery({
    variables: { first: 10 },
    context: {
      requiresAuth: true,
    },
  });

  const isFavorite = (id: any): boolean => {
    if (isLoggedIn) {
      return Array.isArray(favoriteData?.me?.favorites?.edges)
        ? favoriteData!.me!.favorites!.edges!.some(
            (favorite: any) => favorite?.node?.id === id
          )
        : false;
    } else {
      return Array.isArray(guestFavoriteListArray)
        ? guestFavoriteListArray.includes(id)
        : false;
    }
  };

  const isInComparison = Array.isArray(comparison)
    ? comparison.some((item: any) => item.id === id)
    : false;

  const hasMounted = useHasMounted();

  const handleSuccess = (data: AddFavoriteMutation) => {
    if (data?.addFavorite?.errors?.length === 0) {
      setFavorites(data?.addFavorite?.user?.favorites?.edges || []);
    }
  };

  const handleDelete = (data: RemoveFavoriteMutation) => {
    if (data?.removeFavorite?.errors?.length === 0) {
      setFavorites(data?.removeFavorite?.user?.favorites?.edges || []);
    }
  };

  const [create] = useAddFavoriteMutation({
    onCompleted: handleSuccess,
    context: {
      requiresAuth: true,
    },
  });

  const [remove] = useRemoveFavoriteMutation({
    onCompleted: handleDelete,
    context: {
      requiresAuth: true,
    },
  });

  const handleSuccessComparison = (data: AddVariantToComparisonMutation) => {
    if (data?.addVariantToComparison?.errors?.length === 0) {
      setComparison(data?.addVariantToComparison?.user?.comparison);
    }
  };

  const handleDeleteComparison = (
    data: RemoveVariantFromComparisonMutation
  ) => {
    if (data?.removeVariantFromComparison?.errors?.length === 0) {
      setComparison(data.removeVariantFromComparison?.user?.comparison);
    }
  };

  const addToFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        const result = await create({
          variables: {
            variant: id,
          },
        });
        refetchFavoriteData();
      } else {
        useGuestStore.getState().addToFavorite(id);
      }
    } catch (error) {
      console.error('Error on adding to favorite: ', error);
    }
  };

  const removeFromFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        await remove({
          variables: {
            variant: id,
          },
        });
        refetchFavoriteData();
      } else {
        useGuestStore.getState().removeFromFavorite(id);
      }
    } catch (error) {
      console.error('Error on removing from favorite: ', error);
    }
  };

  const [createComparison, createComparisonOps] =
    useAddVariantToComparisonMutation({
      onCompleted: handleSuccessComparison,
      context: {
        requiresAuth: true,
      },
    });

  const [removeComparison, removeComparisonOps] =
    useRemoveVariantFromComparisonMutation({
      onCompleted: handleDeleteComparison,
      context: {
        requiresAuth: true,
      },
    });

  const addToComparison = async (id: any) => {
    if (!isLoggedIn) {
      useGuestStore.getState().addToComparison(id);

      // refetchGuestComparisonListData();
    } else {
      await createComparison({
        variables: {
          variant: id,
        },
      });
    }
  };

  const removeFromComparison = async (id: any) => {
    if (isLoggedIn) {
      await removeComparison({
        variables: {
          variant: id,
        },
      });
    } else {
      useGuestStore.getState().removeFromComparison(id);
    }
  };

  return (
    <>
      {hasMounted && (
        <Button
          onClick={() =>
            isInComparison ? removeFromComparison(id) : addToComparison(id)
          }
          type="button"
          className={styles.btn}
        >
          {isInComparison ? <IconCompareActive /> : <IconCompare />}
        </Button>
      )}
      <motion.div
        whileTap={{ scale: 2 }}
        transition={{ duration: 0.5 }}
        onClick={() =>
          isFavorite(id) ? removeFromFavorite(id) : addToFavorite(id)
        }
        style={{
          cursor: 'pointer',
        }}
      >
        {isFavorite(id) ? (
          <Button type="button" className={styles.btn}>
            <IoHeartSharp size={25} color="red" />
          </Button>
        ) : (
          <Button type="button" className={styles.btn}>
            {hasMounted && <IoHeartOutline size={25} />}
          </Button>
        )}
      </motion.div>
    </>
  );
}
